/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Marketing landing for Cricket Dashboard (EVM for Civil Construction).
 * Distinct from CricketDashboardPage.jsx, which is the authenticated
 * dashboard rendered inside AuthWrapper for Civil organizations.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const CRICKET_METRICS = [
  {
    icon: '🎯',
    title: 'Target Score',
    body: "PV (Planned Value) — your project's planned spend at this point in time."
  },
  {
    icon: '🏃',
    title: 'Actual Runs Scored',
    body: 'EV (Earned Value) — the value of work actually completed.'
  },
  {
    icon: '💰',
    title: 'Resources Spent',
    body: 'AC (Actual Cost) — what you have actually spent.'
  },
  {
    icon: '📈',
    title: 'Run Rate',
    body: 'SPI / CPI — performance indices showing if you are ahead or behind, on or over budget.'
  }
];

const SHOWS = [
  'Real-time EVM gauges: SPI, CPI, TCPI',
  'S-Curve chart for planned vs earned vs actual',
  'Work package progress tracking',
  'Early warning detection',
  'EAC (Estimate at Completion) and VAC (Variance at Completion)',
  'Mapped to PWD/CPWD RA-Bill cycles for Indian projects'
];

const BUILT_FOR = [
  'PWD/CPWD contractors',
  'Infrastructure developers',
  'Government project teams'
];

const DEMO_MAILTO =
  'mailto:info@skilldelivery.co.in?subject=Cricket%20Dashboard%20-%20demo%20request';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
      }}
    />
  </>
);

const CricketDashboardLanding = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-10">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            EVM for Civil Construction
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Cricket Dashboard 🏏
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Project health tracked like a cricket scorecard. Earned Value Management for civil construction firms — built on 40+ years of PWD/CPWD project experience.
          </p>
          <div className="mt-6 flex justify-center">
            <span className="rounded-brandPill bg-green-50 text-brand-green px-4 py-1 text-xs font-bold uppercase tracking-wider">
              Demo Ready
            </span>
          </div>
        </header>

        {/* ── Hero callout ── */}
        <div className="mt-12 max-w-3xl mx-auto px-6 py-5 rounded-brandCard border-l-4 border-brand-cyan bg-brand-bgOff2">
          <p className="text-base sm:text-lg text-brand-text leading-relaxed italic">
            Because in construction project management, tracking is like a cricket match scorecard.
          </p>
        </div>

        {/* ── Why cricket? section ── */}
        <section className="mt-16">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Why cricket?
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {CRICKET_METRICS.map((m) => (
              <div
                key={m.title}
                className="bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                  <span className="text-2xl leading-none" aria-hidden="true">{m.icon}</span>
                </div>
                <h3 className="font-bold text-base text-brand-text">{m.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── What it shows ── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-text tracking-tight text-center">
            What Cricket Dashboard shows
          </h2>
          <ul className="mt-6 space-y-3">
            {SHOWS.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="text-brand-cyan font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="text-base text-brand-text leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Built for ── */}
        <section className="mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Built for
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {BUILT_FOR.map((s) => (
              <span
                key={s}
                className="rounded-brandPill bg-white border border-brand-border text-brand-text px-4 py-2 text-sm font-medium"
              >
                {s}
              </span>
            ))}
          </div>
        </section>

        {/* ── Availability note ── */}
        <p className="mt-10 text-center text-sm italic text-brand-muted2 max-w-2xl mx-auto">
          Cricket Dashboard is available to Civil Construction organizations. Sign in to your Civil organization account to access it from the sidebar.
        </p>

        {/* ── Final CTA ── */}
        <section className="mt-16 text-center">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Want to see it in action?
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
            Request a demo.
          </h2>
          <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
            We will walk through Cricket Dashboard on a sample PWD/CPWD project — see exactly how it tracks YOUR style of work.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <a
              href={DEMO_MAILTO}
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Request a demo
            </a>
            <Link
              to="/domain/civil"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              See Civil domain
            </Link>
          </div>
        </section>

        {/* ── Inline footer ── */}
        <footer className="text-center mt-16">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default CricketDashboardLanding;
